import React, { useState } from "react";
import axios from "axios";
import { Calendar, CheckCircle2, XCircle, AlertTriangle, Loader2 } from "lucide-react";
import ReserveDateModal from "./ReserveDateModal";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Date availability checker → opens the deposit modal pre-filled when available.
 *
 * Props:
 *  - cruiseType:  internal id (wedding|corporate|christmas|byo)
 *  - cruiseLabel: display label
 *  - accent:      tailwind classes for the primary CTA (e.g. "bg-yellow-400 hover:bg-yellow-300 !text-slate-900")
 *  - testid:      base for data-testids
 */
export default function DateAvailabilityChecker({ cruiseType, cruiseLabel, accent = "bg-yellow-400 hover:bg-yellow-300 !text-slate-900", testid = "date-checker" }) {
 const today = new Date().toISOString().slice(0, 10);
 const [pickedDate, setPickedDate] = useState("");
 const [checking, setChecking] = useState(false);
 const [result, setResult] = useState(null); // { available, reason, holiday, holiday_name } | null
 const [showModal, setShowModal] = useState(false);

 const check = async () => {
  if (!pickedDate) return;
  setChecking(true);
  setResult(null);
  try {
   const { data } = await axios.get(`${API}/api/availability/check`, { params: { date: pickedDate } });
   setResult(data);
  } catch (e) {
   setResult({ available: false, reason: e?.response?.data?.detail || "Couldn't check availability. Please try again." });
  } finally {
   setChecking(false);
  }
 };

 const onDateChange = (v) => {
  setPickedDate(v);
  setResult(null);
 };

 return (
  <div className="w-full" data-testid={testid}>
   <div className="text-white/85 text-[11px] uppercase tracking-[0.32em] font-black mb-2.5 flex items-center gap-2">
    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-yellow-400 text-slate-900 text-[10px] font-black">1</span>
    Check date availability
   </div>

   {/* Row: date input + check button */}
   <div className="flex flex-col sm:flex-row gap-2">
    <input
     type="date"
     min={today}
     value={pickedDate}
     onChange={(e) => onDateChange(e.target.value)}
     className="flex-1 px-5 py-4 rounded-full bg-white/95 text-slate-900 text-base font-bold tabular-nums focus:outline-none focus:ring-2 focus:ring-yellow-400"
     data-testid={`${testid}-input`}
    />
    <button
     onClick={check}
     disabled={!pickedDate || checking}
     className={`px-7 py-4 rounded-full font-black text-sm tracking-[0.18em] uppercase shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all whitespace-nowrap inline-flex items-center justify-center gap-2 ${accent.includes("!text-") ? "" : "text-white"} ${accent}`}
     data-testid={`${testid}-check-btn`}
    >
     {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
     {checking ? "Checking…" : "Check"}
    </button>
   </div>

   {/* Result panel */}
   {result && (
    <div className={`mt-3 rounded-2xl px-4 py-3.5 ${result.available ? "bg-emerald-500/15 ring-1 ring-emerald-400/50" : "bg-rose-500/15 ring-1 ring-rose-400/50"}`} data-testid={`${testid}-result`}>
     {result.available ? (
      <div className="space-y-3">
       <div className="inline-flex items-center gap-2 text-emerald-200 font-black text-sm">
        <CheckCircle2 className="w-5 h-5" /> Date is available , reserve it now
       </div>
       {result.holiday && (
        <div className="flex items-start gap-2 text-amber-200 text-xs leading-snug">
         <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Note: {result.holiday_name} is a public holiday. A +20% surcharge applies to the final cruise total (4-hour minimum).
        </div>
       )}
       <div className="pt-2 border-t border-white/10">
        <div className="text-white/70 text-[10px] uppercase tracking-[0.28em] font-black mb-2 flex items-center gap-2">
         <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-yellow-400 text-slate-900 text-[10px] font-black">2</span>
         Lock it in
        </div>
        <button
         onClick={() => setShowModal(true)}
         className={`w-full px-6 py-4 rounded-full font-black text-sm tracking-[0.18em] uppercase shadow-xl transition-all inline-flex items-center justify-center gap-2 hover:scale-[1.02] ${accent.includes("!text-") ? "" : "text-white"} ${accent}`}
         data-testid={`${testid}-reserve-btn`}
        >
         Reserve this date · $1,000 →
        </button>
       </div>
      </div>
     ) : (
      <div className="flex items-start gap-2 text-rose-200 font-bold text-sm">
       <XCircle className="w-5 h-5 mt-0.5 shrink-0" /> <span>{result.reason || "Date unavailable. Please try another date."}</span>
      </div>
     )}
    </div>
   )}

   <ReserveDateModal
    open={showModal}
    onClose={() => setShowModal(false)}
    cruiseType={cruiseType}
    cruiseLabel={cruiseLabel}
    accent={accent}
    initialDate={pickedDate}
   />
  </div>
 );
}
